import { Box, Input, Spinner, StackDivider, useColorModeValue, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "definitions/constants";
import { API_ROUTES } from "definitions/constants/routeConstants";
import useQueryString from "hooks/useQueryString";
import { LegacyTargetType } from "features/RecordTarget/models";
import { ReactNode, useMemo, useState } from "react";
import apiClient from "services/ApiClient";
import { ActionType } from "features/Resources/Shared/models";
import { useFeature } from "hooks/useFeature";
import { User } from "features/User/models";
import lodash from "lodash";

type PersonalizationToken = {
  id: string;
  name: string;
  content: string;
  sgid: string;
};

const SPECIFIC_PERSON_NAMESPACE = "Specific Person";

function TokenItem({
  onClick,
  isSelected,
  children,
}: {
  onClick: () => void;
  isSelected: boolean;
  children: ReactNode;
}) {
  const backgroundColor = useColorModeValue("gray.100", "gray.800");
  return (
    <CDS.Text14
      p={1}
      cursor="pointer"
      onClick={onClick}
      _hover={{ backgroundColor }}
      borderRadius="md"
      backgroundColor={isSelected ? backgroundColor : undefined}
    >
      {children}
    </CDS.Text14>
  );
}

function SpecificPersonStep({
  actionType,
  onSelect,
}: {
  actionType: ActionType;
  onSelect: (token: PersonalizationToken) => void;
}) {
  const [query, setQuery] = useState("");
  const [pickedUser, setPickedUser] = useState<User | undefined>();
  const debouncedQuery = useMemo(() => lodash.debounce((value: string) => setQuery(value), 200), []);

  const { data: users = [], isFetching: usersLoading } = useQuery<User[]>({
    queryKey: [QUERY_KEYS.users, "search", query],
    queryFn: () => apiClient.get<User[]>(`${API_ROUTES.users.search}?q=${encodeURIComponent(query)}`),
    enabled: !pickedUser,
    refetchOnWindowFocus: false,
  });

  const { data: fields = [], isFetching: fieldsLoading } = useQuery<PersonalizationToken[]>({
    queryKey: [QUERY_KEYS.templateVariables, "userMention", pickedUser?.uuid, actionType],
    queryFn: () =>
      apiClient.get<PersonalizationToken[]>(
        `${API_ROUTES.templateVariables.userMention}?user_uuid=${pickedUser?.uuid}&action_type=${actionType}`,
      ),
    enabled: !!pickedUser,
    refetchOnWindowFocus: false,
  });

  if (pickedUser) {
    return (
      <VStack align="stretch" spacing={1} p={1}>
        <CDS.Text12 fontWeight="semibold" color="gray.500" px={1}>
          {`Field for ${pickedUser.fullName}`}
        </CDS.Text12>
        {fieldsLoading && <Spinner size="sm" alignSelf="center" />}
        {fields.map((field) => (
          <TokenItem key={field.id} onClick={() => onSelect(field)} isSelected={false}>
            {field.name.split(" - ").slice(1).join(" - ")}
          </TokenItem>
        ))}
        <CDS.Text10 mt={2} color="blue.500" cursor="pointer" px={1} onClick={() => setPickedUser(undefined)}>
          ← Pick a different user
        </CDS.Text10>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={1} p={1}>
      <Input
        size="sm"
        placeholder="Search users..."
        defaultValue={query}
        onChange={(e) => debouncedQuery(e.target.value)}
      />
      {usersLoading && <Spinner size="sm" alignSelf="center" />}
      {users.map((user) => (
        <TokenItem key={user.id} onClick={() => setPickedUser(user)} isSelected={false}>
          <CDS.Text14 as="span" fontWeight="medium">
            {user.fullName}
          </CDS.Text14>
          {user.title ? (
            <CDS.Text10 as="span" color="gray.500">
              {" "}
              — {user.title}
            </CDS.Text10>
          ) : null}
        </TokenItem>
      ))}
      {!usersLoading && users.length === 0 && (
        <CDS.Text12 color="gray.500" px={1}>
          No matches
        </CDS.Text12>
      )}
    </VStack>
  );
}

export default function PersonalizationTokenMenu({
  actionType,
  targetType,
  onSelect,
}: {
  actionType: ActionType;
  targetType?: LegacyTargetType;
  onSelect: (token: PersonalizationToken) => void;
}) {
  const dividerColor = useColorModeValue("gray.200", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.800");
  const [selectedNamespace, setSelectedNamespace] = useState<string>();
  const { queryString } = useQueryString(undefined, { actionType, targetType });
  const { enabled: specificUserMentionsEnabled } = useFeature("specific_user_mentions");
  const { data } = useQuery<PersonalizationToken[]>({
    queryKey: [QUERY_KEYS.templateVariables, actionType, targetType],
    queryFn: () => apiClient.get(`${API_ROUTES.templateVariables.base}?${queryString}`),
    initialData: [],
    refetchOnWindowFocus: false,
  });
  const namespaces = data.map((token) => token.name.split(" - ")[0]);
  const uniqueNamespaces = Array.from(new Set(namespaces)).sort();
  if (specificUserMentionsEnabled) {
    uniqueNamespaces.unshift(SPECIFIC_PERSON_NAMESPACE);
  }
  const tokensByNamespace = data.reduce(
    (acc, token) => {
      const [namespace] = token.name.split(" - ");
      if (!acc[namespace]) {
        acc[namespace] = [];
      }
      acc[namespace].push(token);
      return acc;
    },
    {} as Record<string, PersonalizationToken[]>,
  );

  return (
    <Box display="flex" height="200px" minWidth="400px">
      <Box width="200px" px={1} borderRight="1px solid" borderColor={borderColor} overflowY="auto">
        <VStack align="stretch" spacing="0" divider={<StackDivider borderColor={dividerColor} mx="6px !important" />}>
          {uniqueNamespaces.map((namespace) => (
            <TokenItem
              key={namespace}
              onClick={() => setSelectedNamespace(namespace)}
              isSelected={namespace === selectedNamespace}
            >
              {namespace}
            </TokenItem>
          ))}
        </VStack>
      </Box>
      <Box width="200px" pl={2} pr={1} flex="1" overflowY="auto">
        {selectedNamespace === SPECIFIC_PERSON_NAMESPACE ? (
          <SpecificPersonStep actionType={actionType} onSelect={onSelect} />
        ) : selectedNamespace ? (
          <VStack align="stretch" spacing="0" divider={<StackDivider borderColor={dividerColor} mx="6px !important" />}>
            {tokensByNamespace[selectedNamespace]?.map((token) => (
              <TokenItem key={token.id} onClick={() => onSelect(token)} isSelected={false}>
                {token.name.split(" - ").slice(1).join(" - ")}
              </TokenItem>
            ))}
          </VStack>
        ) : (
          <CDS.Text14>Select from the left</CDS.Text14>
        )}
      </Box>
    </Box>
  );
}
